<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { availableLanguages, setLocale, getLocale, type SupportedLocale } from '@/i18n'
import { usePlan } from '@/composables/usePlan'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t } = useI18n()

// Plan + credits
const { isFree, isPaid, creditsRemaining, planName, planInfo, fetchPlan, loading: planLoading } = usePlan()
const showCreditsPopover = ref(false)
const creditsPopoverRef = ref<HTMLElement | null>(null)

// Only show billing CTAs to org admins/owners (type <= 2)
const canManageBilling = computed(() => authStore.user?.type != null && authStore.user.type <= 2)

// Credit bar fill %
const creditPct = computed(() => {
  const info = planInfo.value
  if (!info || info.ai_credits === 0) return 0
  return Math.min(100, Math.round((info.credits_remaining / info.ai_credits) * 100))
})

const creditBarColor = computed(() => {
  if (creditPct.value > 50) return 'bg-green-500'
  if (creditPct.value > 20) return 'bg-yellow-500'
  return 'bg-red-500'
})

function toggleCreditsPopover(e: Event) {
  e.stopPropagation()
  showCreditsPopover.value = !showCreditsPopover.value
}

function onDocClickCredits(e: MouseEvent) {
  if (creditsPopoverRef.value && !creditsPopoverRef.value.contains(e.target as Node)) {
    showCreditsPopover.value = false
  }
}

onMounted(async () => {
  await fetchPlan()
  document.addEventListener('click', onDocClickCredits)
})

router.afterEach(() => {
  if (isFree.value || isPaid.value) fetchPlan() // refresh on navigation
})

// On mobile, sidebar starts closed. On desktop, starts open.
const isMobile = ref(window.innerWidth < 768)
const sidebarOpen = ref(!isMobile.value)

// Update on resize
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    const wasMobile = isMobile.value
    isMobile.value = window.innerWidth < 768
    // Auto-close on shrink to mobile, auto-open on expand to desktop
    if (!wasMobile && isMobile.value) sidebarOpen.value = false
    if (wasMobile && !isMobile.value) sidebarOpen.value = true
  })
}

const navigation = computed(() => {
  const items = [
    { name: t('nav.dashboard'), path: '/dashboard', icon: 'home' },
    { name: t('nav.tours'), path: '/tours', icon: 'map' },
    { name: t('nav.statistics'), path: '/statistics', icon: 'chart' },
    { name: 'Analytics', path: '/analytics', icon: 'chart' },
    { name: 'AI Usage', path: '/usage', icon: 'sparkle' },
    { name: t('nav.leads'), path: '/leads', icon: 'users' },
    // Team is visible to all - page handles plan gating
    { name: 'Team', path: '/team', icon: 'team' },
  ]
  // Branding only for admins (Owner/Admin of org)
  if (authStore.isAdmin) {
    items.push({ name: 'Branding', path: '/branding', icon: 'branding' })
  }
  items.push({ name: t('nav.support'), path: '/support', icon: 'support' })
  items.push({ name: t('nav.settings'), path: '/settings', icon: 'settings' })
  return items
})

const isActive = (path: string) => {
  return route.path === path || route.path.startsWith(path + '/')
}

// Close sidebar on navigation (mobile)
router.afterEach(() => {
  if (isMobile.value) sidebarOpen.value = false
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

// Language selector
const langOpen = ref(false)
const currentLang = ref(getLocale())
const currentFlag = () => availableLanguages.find(l => l.code === currentLang.value)?.flag || '🇬🇧'

function switchLang(code: string) {
  setLocale(code as SupportedLocale)
  currentLang.value = code as SupportedLocale
  langOpen.value = false
}
</script>

<template>
  <div class="min-h-screen bg-background">
    <!-- Mobile backdrop -->
    <div
      v-if="isMobile && sidebarOpen"
      class="fixed inset-0 z-30 bg-black/50 transition-opacity"
      @click="sidebarOpen = false"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-40 bg-card border-r transition-all duration-300',
        isMobile
          ? (sidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full')
          : (sidebarOpen ? 'w-64' : 'w-16')
      ]"
    >
      <!-- Logo -->
      <div class="h-16 flex items-center px-4 border-b">
        <router-link to="/dashboard" class="flex items-center gap-2">
          <img src="/vte-logo-blue-500.jpg" alt="Virtual Tours Easy — Create your 360° virtual tours in 5min" title="Virtual Tours Easy" class="h-8 w-auto rounded" />
        </router-link>
      </div>

      <!-- Navigation -->
      <nav class="p-4 space-y-1">
        <router-link
          v-for="item in navigation"
          :key="item.path"
          :to="item.path"
          :class="[
            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            isActive(item.path)
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          ]"
        >
          <!-- Icons -->
          <svg v-if="item.icon === 'home'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <svg v-if="item.icon === 'map'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <svg v-if="item.icon === 'chart'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <svg v-if="item.icon === 'users'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <svg v-if="item.icon === 'team'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <svg v-if="item.icon === 'branding'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          <svg v-if="item.icon === 'support'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <svg v-if="item.icon === 'settings'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <svg v-if="item.icon === 'sparkle'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
          <span v-if="sidebarOpen">{{ item.name }}</span>
        </router-link>

        <!-- Admin Link (only for admins) -->
        <template v-if="authStore.isAdmin">
          <div class="my-3 border-t"></div>
          <router-link
            to="/admin"
            :class="[
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              isActive('/admin')
                ? 'bg-orange-500 text-white'
                : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
            ]"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span v-if="sidebarOpen">{{ t('nav.admin') }}</span>
          </router-link>
        </template>
      </nav>

      <!-- User section at bottom -->
      <div class="absolute bottom-0 left-0 right-0 p-4 border-t">
        <div v-if="sidebarOpen" class="flex items-center gap-3 mb-3">
          <div class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span class="text-sm font-medium">{{ authStore.fullName?.charAt(0) || authStore.user?.firstName?.charAt(0) || 'U' }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate">{{ authStore.fullName || t('common.user') }}</p>
            <p class="text-xs text-muted-foreground truncate">{{ authStore.user?.email }}</p>
          </div>
        </div>
        <Button
          variant="outline"
          :class="sidebarOpen ? 'w-full' : 'w-8 h-8 p-0'"
          @click="handleLogout"
        >
          <svg class="w-4 h-4" :class="{ 'mr-2': sidebarOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span v-if="sidebarOpen">{{ t('auth.logout') }}</span>
        </Button>
      </div>
    </aside>

    <!-- Main content -->
    <div :class="['transition-all duration-300', isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')]">
      <!-- Top bar -->
      <header class="h-16 border-b bg-card flex items-center px-4 gap-4">
        <button
          @click="toggleSidebar"
          class="p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div class="flex-1"></div>

        <!-- ── Subscribe / Upgrade CTA (billing admins only) ── -->
        <template v-if="canManageBilling && !planLoading">
          <!-- Free plan: prominent bordered banner -->
          <router-link
            v-if="isFree"
            to="/settings/subscription"
            class="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md border-2 border-red-400 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors text-sm font-medium"
          >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            {{ t('credits.subscribeCta') }}
          </router-link>
          <!-- Paid plan: subtle upgrade nudge -->
          <router-link
            v-else
            to="/settings/subscription"
            class="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/30 text-primary hover:bg-primary/5 transition-colors text-sm font-medium"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            {{ t('credits.upgradeCta') }}
          </router-link>
        </template>

        <!-- ── AI Credits chip ── -->
        <div v-if="!planLoading" class="relative" ref="creditsPopoverRef">
          <button
            @click="toggleCreditsPopover"
            class="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-muted transition-colors text-sm border"
            :class="creditsRemaining === 0 ? 'border-red-300 text-red-600' : 'border-border text-foreground'"
            :title="t('credits.aiCredits')"
          >
            <span class="text-base leading-none">⚡</span>
            <span class="font-semibold tabular-nums">{{ creditsRemaining.toLocaleString() }}</span>
            <span class="text-muted-foreground hidden sm:inline text-xs">{{ t('credits.label') }}</span>
          </button>

          <!-- Credits popover -->
          <div
            v-if="showCreditsPopover"
            class="absolute right-0 top-full mt-2 w-72 rounded-xl border bg-background shadow-xl z-50 overflow-hidden"
          >
            <!-- Header -->
            <div class="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
              <div class="font-semibold text-sm flex items-center gap-2">
                <span>⚡</span> {{ t('credits.aiCredits') }}
              </div>
              <span class="text-xs px-2 py-0.5 rounded-full font-medium"
                :class="isFree ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'">
                {{ planName }} Plan
              </span>
            </div>

            <div class="p-4 space-y-4">
              <!-- Credit bar -->
              <div class="space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-muted-foreground">{{ t('credits.remaining') }}</span>
                  <span class="font-semibold">
                    {{ creditsRemaining.toLocaleString() }}
                    <span class="text-muted-foreground font-normal">/ {{ (planInfo?.ai_credits ?? 0).toLocaleString() }}</span>
                  </span>
                </div>
                <div class="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    class="h-full rounded-full transition-all"
                    :class="creditBarColor"
                    :style="`width: ${creditPct}%`"
                  />
                </div>
                <div class="flex justify-between text-xs text-muted-foreground">
                  <span>{{ t('credits.used', { n: (planInfo?.credits_used_this_month ?? 0).toLocaleString() }) }}</span>
                  <span v-if="(planInfo?.bonus_credits ?? 0) > 0" class="text-yellow-600">
                    {{ t('credits.bonus', { n: planInfo?.bonus_credits.toLocaleString() }) }}
                  </span>
                </div>
              </div>

              <!-- Credit pricing note -->
              <div class="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 space-y-1">
                <div class="flex justify-between">
                  <span>{{ t('credits.pricingPhoto360') }}</span>
                  <span class="font-medium">10 {{ t('credits.label') }}</span>
                </div>
                <div class="flex justify-between">
                  <span>{{ t('credits.pricingEnhance') }}</span>
                  <span class="font-medium">5 {{ t('credits.label') }}</span>
                </div>
                <div class="flex justify-between">
                  <span>{{ t('credits.pricingFloorPlan') }}</span>
                  <span class="font-medium">5 {{ t('credits.label') }}</span>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex gap-2">
                <router-link
                  to="/usage"
                  @click="showCreditsPopover = false"
                  class="flex-1 text-center text-sm px-3 py-2 rounded-lg border hover:bg-muted transition-colors"
                >
                  {{ t('credits.viewUsage') }}
                </router-link>
                <router-link
                  to="/settings/subscription"
                  @click="showCreditsPopover = false"
                  class="flex-1 text-center text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium"
                >
                  {{ isFree ? t('credits.subscribe') : t('credits.topUp') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Language Selector -->
        <div class="relative">
          <button
            @click="langOpen = !langOpen"
            class="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted transition-colors text-sm"
          >
            <span class="text-base">{{ currentFlag() }}</span>
            <svg class="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
          </button>
          <div
            v-if="langOpen"
            class="absolute right-0 top-full mt-1 bg-popover border rounded-md shadow-lg py-1 z-50 min-w-[140px]"
          >
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              @click="switchLang(lang.code)"
              :class="[
                'w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors text-left',
                lang.code === currentLang ? 'font-medium text-foreground' : 'text-muted-foreground'
              ]"
            >
              <span>{{ lang.flag }}</span>
              <span>{{ lang.nativeName }}</span>
            </button>
          </div>
        </div>

        <!-- Quick actions -->
        <Button size="sm" as-child>
          <router-link to="/tours" class="flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <span class="hidden sm:inline">{{ t('nav.newTour') }}</span>
          </router-link>
        </Button>
      </header>

      <!-- Page content -->
      <main>
        <slot />
      </main>
    </div>
  </div>
</template>
